import { useState, useEffect, useMemo } from "react";
import Field from "./field";
import Checkbox from "./checkbox";

interface JsonSchemaProperty {
  type?: string;
  title?: string;
  description?: string;
  default?: unknown;
  enum?: string[];
}

interface JsonSchema {
  type?: string;
  properties?: Record<string, JsonSchemaProperty>;
  required?: string[];
}

interface DynamicJsonFormProps {
  schema: string;
  value: string;
  onChange: (value: string) => void;
  /**
   * Show "••••••••" as placeholder on empty text fields — for editing setups whose
   * saved values the backend never returns, so blank inputs don't read as "unconfigured".
   */
  maskPlaceholders?: boolean;
}

export default function DynamicJsonForm({
  schema,
  value,
  onChange,
  maskPlaceholders = false,
}: DynamicJsonFormProps) {
  const parsedSchema = useMemo(() => {
    try {
      return JSON.parse(schema) as JsonSchema;
    } catch {
      return null;
    }
  }, [schema]);

  const [values, setValues] = useState<Record<string, unknown>>({});

  // Initialize values from schema defaults and existing value
  useEffect(() => {
    if (!parsedSchema?.properties) return;

    const defaults: Record<string, unknown> = {};
    Object.entries(parsedSchema.properties).forEach(([key, prop]) => {
      if (prop.default !== undefined) {
        defaults[key] = prop.default;
      } else if (prop.type === "boolean") {
        defaults[key] = false;
      } else {
        defaults[key] = "";
      }
    });

    // Merge with existing value if valid
    try {
      const existing = JSON.parse(value);
      if (typeof existing === "object" && existing !== null) {
        Object.assign(defaults, existing);
      }
    } catch {
      // ignore invalid JSON
    }

    setValues(defaults);
  }, [parsedSchema, schema]);

  // Don't render if no valid schema
  if (!parsedSchema?.properties) {
    return (
      <p className="text-sm text-foreground/40">
        No hay un esquema de configuración definido para este proveedor.
      </p>
    );
  }

  const handleChange = (key: string, val: unknown) => {
    const next = { ...values, [key]: val };
    setValues(next);
    onChange(JSON.stringify(next));
  };

  const createChangeHandler =
    (key: string) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const prop = parsedSchema.properties![key];
      let val: unknown = e.target.value;
      if (prop?.type === "number" || prop?.type === "integer") {
        val = e.target.value === "" ? "" : Number(e.target.value);
      }
      handleChange(key, val);
    };

  return (
    <div className="space-y-4">
      {Object.entries(parsedSchema.properties).map(([key, prop]) => {
        const label = prop.title || key;
        const isRequired = parsedSchema.required?.includes(key);
        const currentValue =
          values[key] ?? (prop.type === "boolean" ? false : "");

        if (prop.type === "boolean") {
          return (
            <div key={key} className="py-2">
              <Checkbox
                label={label}
                checked={!!currentValue}
                onChange={(checked) => handleChange(key, checked)}
              />
            </div>
          );
        }

        if (prop.enum && prop.enum.length > 0) {
          return (
            <Field
              key={key}
              label={label}
              name={key}
              type="select"
              value={currentValue as string | number}
              onChange={createChangeHandler(key)}
              options={prop.enum.map((v) => ({ value: v, label: v }))}
              required={isRequired}
            />
          );
        }

        return (
          <Field
            key={key}
            label={label}
            name={key}
            type={
              prop.type === "number" || prop.type === "integer"
                ? "number"
                : "text"
            }
            value={currentValue as string | number}
            onChange={createChangeHandler(key)}
            placeholder={maskPlaceholders ? "••••••••••••" : prop.description || ""}
            required={isRequired && !maskPlaceholders}
          />
        );
      })}
    </div>
  );
}
